/**
 * V1 feature flags.
 *
 * Single source of truth for scope gates that aren't part of the
 * permanent product surface. Each flag corresponds to a specific
 * spec-defined deferral; flipping a flag = an intentional product
 * decision, not a routine config change.
 *
 * Wiring this to LaunchDarkly (per `docs/spec/01-tech-stack.md`) is
 * a follow-up — for v1 launch we read static defaults compiled into
 * the bundle. Tests use `setFeatureOverride` / `resetFeatures` to
 * exercise both branches without monkey-patching imports.
 *
 * @see docs/spec/22a-v1-launch-posture.md
 */

export interface Features {
  /**
   * V1 launch posture (decided 2026-05-13): player accounts for
   * under-13s are deferred to v2. While this flag is `false`:
   *   - The age gate returns `UNDER_13_DEFERRED` for any DoB < 13.
   *   - No VPC / parental-consent flow runs.
   *   - No Yoti integration runs.
   *   - Under-13 sign-up shows an informational message: the coach
   *     can still use 4CM for their team, the kid doesn't get an
   *     account yet.
   *
   * Setting this to `true` re-enables the COPPA / UK-GDPR Art 8
   * parental-consent code paths (which exist in the tree but are
   * unreachable in v1).
   */
  under13PlayerAccounts: boolean;
}

const DEFAULTS: Features = {
  under13PlayerAccounts: false,
};

let overrides: Partial<Features> = {};

/**
 * Read the current feature flags. Result is a fresh object — callers
 * must not mutate it; use `setFeatureOverride` for tests.
 */
export function getFeatures(): Features {
  return { ...DEFAULTS, ...overrides };
}

/**
 * Override a single flag. Test-only. Resets via `resetFeatures()`.
 */
export function setFeatureOverride<K extends keyof Features>(
  key: K,
  value: Features[K],
): void {
  overrides[key] = value;
}

/** Reset all overrides back to compiled defaults. Test-only. */
export function resetFeatures(): void {
  overrides = {};
}
